import { BleService } from './bleService';
import { DfuProgress } from './types';
import { inspectBinary } from './binaryValidator';

const CHUNK_SIZE = 128; // 128 bytes per BLE packet for reliable E104-BT52 transmission
const HANDSHAKE_TIMEOUT_MS = 10000;

export class DfuManager {
  private ble: BleService;
  private isAborted = false;
  public isFlashing = false;
  private onProgressCallback?: (p: DfuProgress) => void;

  constructor(ble: BleService) {
    this.ble = ble;
  }

  public setProgressCallback(cb: (p: DfuProgress) => void) {
    this.onProgressCallback = cb;
  }

  private report(p: Partial<DfuProgress>) {
    if (this.onProgressCallback) {
      this.onProgressCallback({
        stage: p.stage || 'STREAMING',
        percent: p.percent ?? 0,
        bytesSent: p.bytesSent ?? 0,
        totalBytes: p.totalBytes ?? 0,
        speedBps: p.speedBps ?? 0,
        etaSeconds: p.etaSeconds ?? 0,
        message: p.message || '',
        error: p.error
      });
    }
  }

  public abort() {
    this.isAborted = true;
    this.report({
      stage: 'ERROR',
      message: 'DFU update aborted by user.',
      error: 'Aborted'
    });
  }

  public async flashFirmware(binaryBuffer: ArrayBuffer): Promise<void> {
    this.isAborted = false;
    this.isFlashing = true;
    const totalBytes = binaryBuffer.byteLength;

    // 1. Sanity check
    this.report({
      stage: 'VALIDATING_BIN',
      message: 'Inspecting ARM Cortex-M0+ vector table and binary headers...',
      percent: 0,
      totalBytes
    });

    const info = inspectBinary(binaryBuffer);
    if (!info.valid) {
      this.report({
        stage: 'ERROR',
        message: `Binary verification failed: ${info.error}`,
        error: info.error
      });
      throw new Error(info.error);
    }

    if (!this.ble.isConnected()) {
      throw new Error('Device is not connected');
    }

    // 2. Set up JSON listener for DFU responses
    let handshakeBegun = false;
    let upgradeOk = false;
    let upgradeFailedError: string | null = null;
    const ackQueue: number[] = [];
    let ackWaiter: ((rxLen: number) => void) | null = null;
    let ackRejecter: ((err: Error) => void) | null = null;

    const removeJsonListener = this.ble.addJsonListener((obj) => {
      if (obj.info === 'upgrade') {
        if (obj.state === 'begin') {
          handshakeBegun = true;
        } else if (obj.state === 'ok') {
          upgradeOk = true;
          if (ackWaiter) {
            const cb = ackWaiter;
            ackWaiter = null;
            ackRejecter = null;
            cb(0);
          }
        } else if (obj.state === 'failed!') {
          const errMsg = `Device reported upgrade failed (state: failed!)`;
          upgradeFailedError = errMsg;
          if (ackRejecter) {
            const rj = ackRejecter;
            ackWaiter = null;
            ackRejecter = null;
            rj(new Error(errMsg));
          }
        }
      }

      if (typeof obj.upgrade_index === 'number') {
        this.ble.log(`Device reported flash progress: ${obj.upgrade_index} / ${obj.upgrade_len ?? totalBytes} bytes`, 'info');
      }

      if (typeof obj.rx_len === 'number') {
        if (ackWaiter) {
          const cb = ackWaiter;
          ackWaiter = null;
          ackRejecter = null;
          cb(obj.rx_len);
        } else {
          ackQueue.push(obj.rx_len);
        }
      }
    });

    const waitForAck = (timeoutMs: number): Promise<number> => {
      if (upgradeFailedError) {
        return Promise.reject(new Error(upgradeFailedError));
      }
      if (upgradeOk) {
        return Promise.resolve(0);
      }
      if (ackQueue.length > 0) {
        return Promise.resolve(ackQueue.shift()!);
      }
      return new Promise<number>((resolve, reject) => {
        const timer = setTimeout(() => {
          ackWaiter = null;
          ackRejecter = null;
          reject(new Error('TIMEOUT'));
        }, timeoutMs);

        ackWaiter = (rxLen: number) => {
          clearTimeout(timer);
          resolve(rxLen);
        };

        ackRejecter = (err: Error) => {
          clearTimeout(timer);
          reject(err);
        };
      });
    };

    try {
      // 3. Handshake & Flash Erase
      this.report({
        stage: 'HANDSHAKE',
        message: `Sending handshake (Size: ${totalBytes} bytes). Waiting for flash erase...`,
        percent: 0,
        totalBytes
      });

      await this.ble.sendJson({
        info: 'upgrade',
        len: totalBytes
      });

      // Wait for "state":"begin"
      const startTime = Date.now();
      while (!handshakeBegun) {
        if (this.isAborted) return;
        if (upgradeFailedError) throw new Error(upgradeFailedError);
        if (Date.now() - startTime > HANDSHAKE_TIMEOUT_MS) {
          const err = 'Device handshake timed out (device did not respond with begin).';
          this.report({ stage: 'ERROR', message: err, error: err });
          throw new Error(err);
        }
        await new Promise((r) => setTimeout(r, 50));
      }

      this.report({
        stage: 'STREAMING',
        message: 'Flash erased. Starting binary streaming...',
        percent: 0,
        totalBytes
      });

      // 4. Stream raw chunks sequentially with inter-chunk pacing
      const bytes = new Uint8Array(binaryBuffer);
      let offset = 0;
      const streamStart = Date.now();
      const CHUNK_TIMEOUT_MS = 1200; // Normal ACK is ~60ms; 1200ms handles occasional flash page delays

      while (offset < totalBytes) {
        if (this.isAborted) return;
        if (upgradeFailedError) throw new Error(upgradeFailedError);
        if (upgradeOk) break;

        const chunkEnd = Math.min(offset + CHUNK_SIZE, totalBytes);
        const chunk = bytes.slice(offset, chunkEnd);
        const chunkLen = chunk.byteLength;

        // Write binary chunk over BLE with guaranteed ATT Write Request (writeValueWithResponse)
        await this.ble.write(chunk);

        // Await chunk acknowledgment {"rx_len": ...}
        // Note: writeValueWithResponse guarantees link-layer delivery of the chunk to the MCU.
        // If the unacknowledged BLE notification {"rx_len": 128} from the MCU is dropped over RF,
        // we MUST NOT resend the chunk, because the MCU has no sequence numbering or deduplication
        // and would write duplicate bytes, shifting the firmware offset and exceeding upgrade_len.
        // If waitForAck times out, we know the chunk was received, so we safely advance.
        try {
          await waitForAck(CHUNK_TIMEOUT_MS);
        } catch (e) {
          if (this.isAborted) return;
          if (upgradeFailedError) throw new Error(upgradeFailedError);
          if (upgradeOk) break;

          this.ble.log(`RF notification lost at byte ${offset}. Chunk delivered, advancing stream...`, 'info');
        }

        offset += chunkLen;

        // Pacing delay (20ms) to give the E104-BT52 BLE module and STM32 UART DMA time to process and re-arm
        if (offset < totalBytes) {
          await new Promise((r) => setTimeout(r, 20));
        }

        // Stats calculation
        const elapsedSec = (Date.now() - streamStart) / 1000;
        const speedBps = elapsedSec > 0 ? Math.round(offset / elapsedSec) : 0;
        const remainingBytes = totalBytes - offset;
        const etaSeconds = speedBps > 0 ? Math.ceil(remainingBytes / speedBps) : 0;
        const percent = Math.min(100, Math.round((offset / totalBytes) * 100));

        this.report({
          stage: 'STREAMING',
          percent,
          bytesSent: offset,
          totalBytes,
          speedBps,
          etaSeconds,
          message: `Flashing... ${percent}% (${(offset / 1024).toFixed(1)} / ${(totalBytes / 1024).toFixed(1)} KB)`
        });
      }

      // 5. Finalizing & Reboot
      this.ble.isRebootingAfterFlash = true;

      this.report({
        stage: 'FINALIZING',
        percent: 100,
        bytesSent: totalBytes,
        totalBytes,
        message: 'All bytes delivered (100%). Waiting for bootloader jump...'
      });

      const finalizeStart = Date.now();
      while (!upgradeOk) {
        if (upgradeFailedError) throw new Error(upgradeFailedError);
        if (Date.now() - finalizeStart > 3000) {
          break; // Device triggers system reset immediately
        }
        await new Promise((r) => setTimeout(r, 50));
      }

      this.report({
        stage: 'COMPLETED',
        percent: 100,
        bytesSent: totalBytes,
        totalBytes,
        message: 'Firmware installed (100%)! Device is rebooting.'
      });
    } finally {
      this.isFlashing = false;
      removeJsonListener();
    }
  }
}
